import { assertErrors, fetchAPI, METHODS, parseBody } from './common';
import qs from 'qs';
import { BUCKET_NAME, S3_BASE_URL } from 'src/config.js';

/**
 * @typedef TeamMember
 * @property {string} email
 * @property {string} fullName
 * @property {number} type 0 = staff, 1 = driver
 * @property {string} teamId
 */

const mapTeamMemberDocUrl = (member) => ({
  ...member,
  doc_url:
    member.doc_file_name && member.doc_key_name
      ? `${S3_BASE_URL}/${BUCKET_NAME}/${member.doc_key_name}`
      : null,
});

/**
 * Gets all team members in user's team.
 * @param {"ALL" | "INDIVIDUAL"} type
 * @returns Promise<TeamMember[]>
 */
export async function get({ type = 'ALL' } = {}) {
  const res = await fetchAPI(
    `/user/team-members?${qs.stringify({ type })}`,
    METHODS.get
  );
  const parsedBody = await parseBody(res);
  if (parsedBody.length) {
    return parsedBody.map(mapTeamMemberDocUrl);
  }
  return [];
}

/**
 * Gets paginated team members with filter model support.
 * @param {number} step
 * @param {number} offset
 * @param {FilterModel} filterModel
 * @param {'ALL' | 'INDIVIDUAL'} [type] omit for every member
 * @param {Array<{field: string, sort: 'asc' | 'desc'}>} [sortModel]
 * @returns {Promise<{results: TeamMember[], count: number}>} Promise resolving to an object with results and count
 */
export async function getPaginated({
  step = 25,
  offset = 0,
  filterModel,
  includeLastActivity = false,
  includeJobTitle = false,
  type,
  sortModel,
} = {}) {
  const res = await fetchAPI(
    `/user/team-members/list?${qs.stringify({
      step,
      offset,
      includeLastActivity,
      includeJobTitle,
      type,
    })}`,
    METHODS.post,
    { filterModel, sortModel }
  );
  const parsedBody = await parseBody(res);
  if (parsedBody.results) {
    return {
      results: parsedBody.results.map(mapTeamMemberDocUrl),
      count: parsedBody.count,
    };
  }
  return { results: [], count: 0 };
}

/**
 * Invites a new team member to the user's team.
 * @param {TeamMember} teamMember
 */
export async function add(data) {
  const res = await fetchAPI('/user/add-team-member', METHODS.post, data);
  await parseBody(res);
}

/**
 * Updates the team user's type - staff <-> driver.
 * @param {string} teamUserId
 * @param {boolean} isDriver
 */
export async function updateUserType(teamUserId, isDriver) {
  const res = await fetchAPI('/user/teamuser', METHODS.post, {
    teamUserId,
    isDriver,
  });
  await parseBody(res);
}

/**
 * Updates the team user's perms - promote/demote.
 * @param {string} param.id
 * @param {string} param.teamId
 * @param {boolean} param.isAdmin
 * @param {boolean} param.isDriver
 */
export async function updateUserPerms({ id, teamId, isAdmin, isDriver }) {
  const res = await fetchAPI('/user/teamuser-perms', METHODS.post, {
    teamUserId: id,
    isAdmin,
    isDriver,
    teamId,
  });
  await parseBody(res);
}

/**
 * Set's the user's `is_team_user_active` status for the current team.
 * @param {string} teamUserId
 * @param {boolean} isActive
 */
export async function setActive(teamUserId, isActive) {
  const res = await fetchAPI('/user/set-active-teamuser', METHODS.post, {
    teamUserId,
    isActive,
  });
  await parseBody(res);
}

/**
 * Removes the team user from the user's team
 * @param {string} teamUserId
 */
export async function remove(teamUserId) {
  const res = await fetchAPI('/user/delete-teamuser', METHODS.delete, {
    teamUserId,
  });
  await parseBody(res);
}

export const EMAIL_TYPE = {
  WELCOME: 'WELCOME',
};

/**
 * Fetches the current team's email template.
 * @param {string} type
 * @returns Promise<string>
 */
export async function getEmailTemplate(type) {
  const res = await fetchAPI(`/team/email/${type}`, METHODS.get);
  const { body } = await parseBody(res);
  return body;
}

export async function driversReports({ filterModel, ...data } = {}) {
  const res = await fetchAPI(
    `/team/drivers-reports/?${qs.stringify(data)}`,
    METHODS.post,
    {
      filterModel,
    }
  );
  return await parseBody(res);
}

/**
 * Updates the current team's email template.
 * @param {string} type
 * @param {string} data
 * @returns Promise<Template>
 */
export async function updateEmailTemplate(type, body) {
  await fetchAPI(`/team/email/${type}`, METHODS.post, { body });
}

export async function resetEmailTemplate(type) {
  const res = await fetchAPI(`/team/email/${type}`, METHODS.delete);
  const { body } = await parseBody(res);
  return body;
}

/**
 * @typedef UpdateTeamUser
 * @property {string} userId
 * @property {string} phone
 * @property {number} lat
 * @property {number} lng
 */

/**
 * Update a team user's data.
 * @param {UpdateTeamUser} data
 */
export async function updateTeamUser({ userId, name, email, phone, lat, lng }) {
  await fetchAPI('/user/teamuser/update', METHODS.post, {
    userId,
    name,
    email,
    phone,
    lat,
    lng,
  });
}

/**
 * Update the notes for a team user.
 * @param {string} userId
 * @param {string} notes
 */
export async function updateTeamUserNotes(userId, notes) {
  await fetchAPI('/user/teamuser/notes', METHODS.post, { userId, notes });
}

/**
 * Set a member's job title in the current organization. A blank title clears it.
 * @param {string} userId
 * @param {string} jobTitle
 */
export async function updateTeamUserJobTitle(userId, jobTitle) {
  const res = await fetchAPI('/user/teamuser/job-title', METHODS.put, {
    userId,
    jobTitle,
  });
  return parseBody(res);
}

/**
 * The signed-in user's job title in the current organization.
 * @returns {Promise<{jobTitle?: string | null, canEdit: boolean}>}
 */
export async function getOwnJobTitle() {
  const res = await fetchAPI('/user/teamuser/job-title', METHODS.get);
  return parseBody(res);
}

/**
 * Update the notes for a team user.
 * @param {object} weekTimesObj
 */
export async function updateOperationalHours(weekTimesObj) {
  await fetchAPI('/team/hours', METHODS.put, { weekTimesObj });
}

/**
 * Update the rescue hours for a team.
 * @param {object} weekTimesObj
 */
export async function updateRescueHours(weekTimesObj) {
  await fetchAPI('/team/rescue/hours', METHODS.put, { weekTimesObj });
}

export async function updateSafetyProgramEnabled({ isSafetyProgramEnabled }) {
  const res = await fetchAPI('/team/business/safety-program', METHODS.put, {
    isSafetyProgramEnabled,
  });
  const body = await parseBody(res);
  if (res.status !== 200) {
    throw new Error(body?.message ?? res.statusText);
  }
  return body;
}

/**
 * Resend an invite to an invited team user.
 * @param {string} userId
 */
export async function resendInvite(userId) {
  await fetchAPI('/team/resend-invite', METHODS.post, { userId });
}

/**
 * @typedef SearchResult
 * @property {string} id
 * @property {"DONATION" | "RESCUE" | "FOODRUN" | "ORGANIZATION" | "USER"} type
 * @property {string} primary Primary "title" of the item.
 * @property {string} secondary A "description" of the item providing some futher detail.
 */

/**
 * Preform a query of all resources accessible to the current user.
 * @param {string} text Textual query of recources
 * @returns SearchResult[]
 */
export async function search(text) {
  const query = qs.stringify({ text });
  const res = await fetchAPI(`/search?${query}`, METHODS.get);
  const results = await parseBody(res);
  return results;
}

/**
 * Get the name of an organzation with the given `teamId`.
 * Will throw an error if the `teamId` is not found.
 * @param {string} teamId
 * @returns Promise<string>
 */
export async function nameOf(teamId) {
  const res = await fetchAPI(`/team/name/${teamId}`, METHODS.get);
  await assertErrors(res);
  const name = await res.text();
  return name;
}

/**
 * Get all of my orgs of a given type.
 * @param {"DONOR" | "RECIPIENT"} type Type of orgs to return.
 * @returns {Promise<Team>}
 */
export async function getMyOrgs(type, args = {}) {
  const res = await fetchAPI(
    `/team/my?${qs.stringify({ type, ...args })}`,
    METHODS.get
  );
  const { teams } = await parseBody(res);
  return teams;
}

/**
 * Add an org to my orgs list.
 * An error will be thrown if the org already exists on the my orgs list.
 * @param {string} param.teamId id of team to be added to the list
 * @param {boolean} param.isDonor if true , it is added to My Donors or else added to My Recipients
 */
export async function addToMyOrgs({ teamId, isDonor = true }) {
  const payload = { isDonor };
  const res = await fetchAPI(
    `/team/my/${teamId}?${qs.stringify(payload)}`,
    METHODS.post
  );
  await assertErrors(res);
}

/**
 * Remove an org from my orgs list.
 * An error will be thrown if the org does not exist on the my orgs list.
 * @param {string} param.teamId id of team to be removed from the list
 * @param {boolean} param.isDonor if true , delete from my donors list, or else delete from my recipients list
 * @returns {Promise<void>}
 */
export async function removeFromMyOrgs({ teamId, isDonor }) {
  const payload = { isDonor };
  const res = await fetchAPI(
    `/team/my/${teamId}?${qs.stringify(payload)}`,
    METHODS.delete
  );
  await assertErrors(res);
}

/**
 * Update the custom metatadata for a given teamId
 * @param {Metadata} metadata
 * @param {string} teamId
 * @returns {Promise<void>}
 */
export async function updateMetadata(metadata, teamId) {
  const res = await fetchAPI(`/team/metadata/${teamId}`, METHODS.post, {
    metadata,
  });
  await assertErrors(res);
}

/**
 * Get team member details
 * @param {string} id
 */
export async function getTeamMember(id) {
  const res = await fetchAPI(`/team/user/${id}`, METHODS.get);
  return parseBody(res, true);
}

/**
 * Get all corporate onboarding requests for this team.
 * @returns {Promise<OnboardingRequest[]>}
 */
export async function getAllOnboardingRequests() {
  const res = await fetchAPI('/team/corp-onboarding/requests', METHODS.get);
  await assertErrors(res);
  return parseBody(res, false, []);
}

/**
 * Count the incomplete onboarding requests for a single npo.
 * @returns {Promise<{ count: number }>}
 */
export async function countOpenOnboardingRequests() {
  const res = await fetchAPI(`/team/corp-onboarding/requests`, 'get');
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get all paginated items (without details).
 * @param {object} opts
 * @param {number?} opts.step  Set to -1 or leave undefined to disable pagination
 * @param {number?} opts.offset
 * @returns {Promise<PaginatedOnboardingItems>}
 */
export async function getOnboardingProgressForBusiness({
  step = -1,
  offset = -1,
  filterModel,
}) {
  const res = await fetchAPI(
    `/team/corp-onboarding/progress/get?${qs.stringify({
      step,
      offset,
    })}`,
    METHODS.post,
    { filterModel }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get all paginated onboarding requests for an NPO with progress information.
 * @param {object} opts
 * @param {string} opts.teamId Team id of the NPO
 * @param {number?} opts.step  Set to -1 or leave undefined to disable pagination
 * @param {number?} opts.offset
 * @returns {Promise<PaginatedOnboardingItems>}
 */
export async function getOnboardingRequestsProgressForBusiness({
  teamId,
  step = -1,
  offset = -1,
  filterModel,
}) {
  const res = await fetchAPI(
    `/team/corp-onboarding/progress/${teamId}/requests/get?${qs.stringify({
      step,
      offset,
    })}`,
    METHODS.post,
    { filterModel }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get all paginated items (without details).
 * @param {object} opts
 * @param {number?} opts.step  Set to -1 or leave undefined to disable pagination
 * @param {number?} opts.offset
 * @param {string} opts.corporateTeamId
 * @returns {Promise<PaginatedOnboardingItems>}
 */
export async function getAllOnboardingItems({
  step = -1,
  offset = -1,
  filterModel,
  corporateTeamId,
  complianceProfileId,
}) {
  const res = await fetchAPI(
    `/team/corp-onboarding/items/${corporateTeamId}/${complianceProfileId}/get?${qs.stringify(
      {
        step,
        offset,
      }
    )}`,
    METHODS.post,
    { filterModel }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get the details for one onboarding item.
 * @param {string} itemId
 * @returns {Promise<import('./corporate').OnboardingItemDetails>}
 */
export async function getOnboardingItemDetails(itemId) {
  const res = await fetchAPI(
    `/team/corp-onboarding/item/${itemId}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get the details for one onboarding item.
 * @param {string} itemId
 * @returns {Promise<import('./corporate').OnboardingItemDetails>}
 */
export async function getOnboardingItemDetailsWithProgress({
  itemId,
  npoTeamId,
}) {
  const res = await fetchAPI(
    `/team/corp-onboarding/progress/${npoTeamId}/${itemId}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Complete an onboarding item
 * @param {string} itemId
 * @param {object} data  TODO: type annotations
 */
export async function completeOnboardingItem(itemId, data) {
  const res = await fetchAPI(
    `/team/corp-onboarding/item/${itemId}`,
    METHODS.post,
    data
  );
  await assertErrors(res);
}

/**
 * @typedef Feedback
 * @property {string} id
 * @property {string} donor
 * @property {string} donor_formatted_address
 * @property {number} donor_rating
 * @property {string} donor_feedback
 * @property {string} recipient
 * @property {string} recipient_formatted_address
 * @property {number} recipient_rating
 * @property {string} recipient_feedback
 * @property {string} created
 * @property {string} donation_id
 */

/**
 * Get team's feedback
 * @param {number} offset
 * @param {number} step
 * @params {FilterModel} filterModel
 * @params {string} feedbackDirection - "received" or "given"
 * @returns {Promise<Feedback[]>}
 */
export async function getTeamFeedback({
  offset,
  step,
  filterModel,
  feedbackDirection,
  sortModel = [],
}) {
  const params = {
    offset,
    step,
    ...(feedbackDirection && { feedbackDirection }),
  };
  const res = await fetchAPI(
    `/team/feedback?${qs.stringify(params)}`,
    METHODS.post,
    { filterModel, sortModel }
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Insert document data associated with the team
 * @param {string} param0.url
 * @param {string} param0.name
 * @param {number} param0.size
 * @param {boolean} [param0.isSharedWithTeams] corporate accounts only - share
 * the document with every team linked to the corporate
 */
export async function insertTeamDocument({
  url,
  name,
  size,
  isSharedWithTeams = false,
}) {
  const res = await fetchAPI(`/team/document`, METHODS.post, {
    url,
    name,
    size,
    isSharedWithTeams,
  });
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * @typedef Document
 * @property {string} id
 * @property {string} team_id
 * @property {string} key_name
 * @property {string} file_name
 * @property {0|1} is_shared_with_teams shared by a corporate with all its teams
 * @property {0|1} is_owned 1 when the document belongs to the current team, 0
 * when it is shared by a corporate the team is linked to - documents that are
 * not owned cannot be deleted by the team
 * @property {string|null} shared_by_team_name name of the corporate sharing it
 */

/**
 * Fetch team documents
 * @returns {Promise<Document[]>}
 */
export async function getTeamDocuments() {
  const res = await fetchAPI(`/team/document`, METHODS.get);
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Delete team document
 * @param {string} param0.id
 * @param {string} param0.documentKey s3 path of the document
 * @returns {Promise<void>}
 */
export async function deleteTeamDocument({ id, documentKey }) {
  const res = await fetchAPI(`/team/document`, METHODS.delete, {
    id,
    documentKey,
  });
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * @typedef OnboardingItem
 * @property {string} id
 * @property {string} title
 * @property {string} corporate_team_id
 * @property {string?} onboarding_manager_name
 */

/**
 * @typedef OnboardingGatekeeperGroup
 * @property {string} complianceProfileId
 * @property {string} gatekeeperTeamId
 * @property {string} gatekeeperName
 * @property {'CORPORATE' | 'MUNICIPALITY'} gatekeeperType
 * @property {boolean} hasOnboardingRequest
 * @property {boolean} hasIncomingOnboardingRequest
 * @property {number} requiredItemCount
 * @property {OnboardingItem[]} items
 */

/**
 * getItemsRequiredToRescueFrom for the donor of the given donation.
 * @params {string} donationId
 */
export async function getItemsRequiredToRescueDonation(donationId) {
  const res = await fetchAPI(
    `/team/corp-onboarding/items-required-to-rescue-donation/${donationId}`,
    METHODS.get
  );
  await assertErrors(res);
  const {
    requiredItems = [],
    hasIncomingOnboardingRequest = false,
    onboardingGroups = [],
  } = await parseBody(res);
  return {
    requiredItems,
    hasIncomingOnboardingRequest: Boolean(hasIncomingOnboardingRequest),
    onboardingGroups,
  };
}

/**
 * Get corporate onboarding items required in order to rescue from the given team.
 * @params {string} teamId
 * @returns {Promise<{
 *   requiredItems: OnboardingItem[],
 *   hasIncomingOnboardingRequest: boolean,
 *   onboardingGroups: OnboardingGatekeeperGroup[]
 * }>}
 */
export async function getItemsRequiredToRescueFrom(teamId) {
  const res = await fetchAPI(
    `/team/corp-onboarding/items-required-to-rescue-from/${teamId}`,
    METHODS.get
  );
  await assertErrors(res);
  const {
    requiredItems = [],
    hasIncomingOnboardingRequest = false,
    onboardingGroups = [],
  } = await parseBody(res);
  return {
    requiredItems,
    hasIncomingOnboardingRequest: Boolean(hasIncomingOnboardingRequest),
    onboardingGroups,
  };
}

/**
 * Get corporate onboarding items required in order to donate to the given team.
 * @params {string} teamId
 * @returns {Promise<OnboardingItem[]>}
 */
export async function getItemsRequiredToDonateTo(teamId) {
  const res = await fetchAPI(
    `/team/corp-onboarding/items-required-to-donate-to/${teamId}`,
    METHODS.get
  );
  await assertErrors(res);
  const { requiredItems } = await parseBody(res);
  return requiredItems;
}

/**
 * Request onboarding for the provided compliance profile.
 * @param {string} profileId
 * @param {string} [donationId] donation the request was initiated from
 * @returns {Promise<{requestId: string}>}
 */
export async function requestOnboarding(profileId, donationId) {
  const res = await fetchAPI('/team/corp-onboarding/request', METHODS.post, {
    profileId,
    ...(donationId ? { donationId } : {}),
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get details for a rejected onboarding request visible to the current NPO.
 * Returns null when the request does not exist or is not rejected.
 * @param {string} requestId
 * @returns {Promise<object|null>}
 */
export async function getRejectedOnboardingRequestDetails(requestId) {
  const res = await fetchAPI(
    `/team/corp-onboarding/request/${requestId}/rejected`,
    METHODS.get
  );

  if (res.status === 404) {
    return null;
  }

  await assertErrors(res);
  const { request } = await parseBody(res);
  return request || null;
}

/**
 * Get a paginated view of all associated teams for the compliance profile.
 * @param {object} opts
 * @param {import("./rescues").FilterModelObj} opts.filterModel
 * @param {string} opts.corpTeamId
 * @param {string} opts.complianceProfileId
 * @param {number?} opts.step
 * @param {number?} opts.offset
 * @returns {Promise<PaginatedComplianceBusiness>}
 */
export async function getTeamsForComplianceProfile({
  corpTeamId,
  complianceProfileId,
  step = -1,
  offset = -1,
  filterModel,
}) {
  const res = await fetchAPI(
    `/team/corp-onboarding/compliance-profile/teams-of-corp/${corpTeamId}/${complianceProfileId}/get?${qs.stringify(
      {
        step,
        offset,
      }
    )}`,
    METHODS.post,
    { filterModel }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get all paginated items with progress details for a business (without details).
 * @param {object} opts
 * @param {number?} opts.step  Set to -1 or leave undefined to disable pagination
 * @param {number?} opts.offset
 * @param {import('./rescues').FilterModelObj} opts.filterModel
 * @param {string} opts.teamId Id of the business
 * @param {string} opts.id Id of the compliance profile
 * @returns {Promise<PaginatedOnboardingItems>}
 */
export async function getOnboardingProgress({
  step = -1,
  offset = -1,
  filterModel,
  teamId,
  id,
}) {
  const res = await fetchAPI(
    `/team/corp-onboarding/progress/${teamId}/${id}/items/get?${qs.stringify({
      step,
      offset,
    })}`,
    'post',
    { filterModel }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Check if user already exists in the team
 * @param {string} email
 */
export async function checkIfUserExistsInTeam(email) {
  const res = await fetchAPI(
    `/team/user/exists/${encodeURIComponent(email)}`,
    'get'
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function getAPIKeys({ step, offset }) {
  const res = await fetchAPI(
    `/team/api-key?${qs.stringify({ step, offset })}`,
    'get'
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function generateAPIKey() {
  const res = await fetchAPI('/team/api-key', 'post');
  await assertErrors(res);
  const { key } = await parseBody(res);
  return key;
}

export async function revokeAPIKey(id) {
  const res = await fetchAPI(`/team/api-key/${id}`, 'delete');
  await assertErrors(res);
}

/**
 * Get a paginated view of all webhooks under this account.
 *
 * @param {object} param0
 * @param {number} param0.step
 * @param {number} param0.offset
 * @returns {Promise<{ count: number, items: Webhook[] }>}
 */
export async function getWebhooks({ step, offset }) {
  const query = qs.stringify({ step, offset });
  const res = await fetchAPI(`/team/api-webhook?${query}`, 'get');
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Create a new webhook subscription
 *
 * @param {object} webhook
 * @param {string} webhook.url Url of endpoint of webhook -- max 500 chars
 * @param {AllowItem[]} webhook.allowlist List of allowed events
 * @returns {Promise<string>} The webhook secret (viewable only once).
 */
export async function subscribeWebhook({ url, allowlist }) {
  const res = await fetchAPI('/team/api-webhook', 'post', { url, allowlist });
  await assertErrors(res);
  const { secret } = await parseBody(res);
  return secret;
}

/**
 * Update an existing webhook subscription
 *
 * @param {string} id Id of the webhook to update
 * @param {object} webhook
 * @param {string} webhook.url Url of endpoint of webhook -- max 500 chars
 * @param {AllowItem[]} webhook.allowlist List of allowed events
 * @returns {Promise<void>}
 */
export async function updateWebhook(id, { url, allowlist }) {
  const res = await fetchAPI(`/team/api-webhook/${id}`, 'put', {
    url,
    allowlist,
  });
  await assertErrors(res);
}

/**
 * Remove an existing webhook subscription
 *
 * @param {string} id Id of the webhook to remove
 * @returns {Promise<void>}
 */
export async function deleteWebhook(id) {
  const res = await fetchAPI(`/team/api-webhook/${id}`, 'delete');
  await assertErrors(res);
}

/**
 * Get a paginated view of the webhook events for a given webhook subscription.
 *
 * @param {string} subscriptionId
 * @param {object} param0
 * @param {number} param0.step
 * @param {number} param0.offset
 * @returns {Promise<{ count: number, items: Webhook[] }>}
 */
export async function getWebhookEvents(subscriptionId, { step, offset }) {
  const query = qs.stringify({ step, offset });
  const res = await fetchAPI(
    `/team/api-webhook/${subscriptionId}/events?${query}`,
    'get'
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function updateTeamInvites(data) {
  const res = await fetchAPI('/team/invites', METHODS.put, data);
  await assertErrors(res);
}

export async function disableInviteLink(data) {
  const res = await fetchAPI('/team/disable-invite-link', METHODS.post, data);
  await assertErrors(res);
}

export async function checkInviteLinkName(data = {}) {
  const res = await fetchAPI(
    `/team/check-invite-name?${qs.stringify(data)}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function authorizeCanJoin(data = {}) {
  const res = await fetchAPI(`/team/authorize-join-team`, METHODS.post, data);
  await assertErrors(res);
  return parseBody(res);
}

export async function verifyTokenJoinTeam(token) {
  const res = await fetchAPI(
    `/team/verify-token-join-team?token=${token}`,
    'get'
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function donationsReportList({ filterModel, type, ...args }) {
  const res = await fetchAPI(
    `/team/donations-${type}-report-list?${qs.stringify(args)}`,
    'post',
    {
      filterModel,
    }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function donationsReportListExport({
  filterModel,
  type,
  ...args
}) {
  const res = await fetchAPI(
    `/team/donations-${type}-report-list-export?${qs.stringify(args)}`,
    'post',
    {
      filterModel,
    }
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function donationsReportGraph({ type, ...args }) {
  const res = await fetchAPI(
    `/team/donations-${type}-report-graph?${qs.stringify(args)}`,
    'get'
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function donationsDifferenceReportGraph(args) {
  const res = await fetchAPI(
    `/team/donations-difference-report-graph?${qs.stringify(args)}`,
    'get'
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function addSelectedGeofence(data) {
  const res = await fetchAPI('/team/add-selected-geofence', METHODS.post, data);
  await assertErrors(res);
  return parseBody(res);
}

export async function getSelectedGeofences() {
  const res = await fetchAPI('/team/get-selected-geofences', METHODS.get);
  await assertErrors(res);
  const body = await parseBody(res);
  return body?.map((item) => {
    return {
      ...item,
      points: JSON.parse(item.points),
      aabb: JSON.parse(item.aabb),
    };
  });
}

export async function updateDriverStartAddressRequired(args) {
  const res = await fetchAPI(
    '/team/driver-start-address-required',
    METHODS.put,
    args
  );
  await assertErrors(res);
  return await parseBody(res);
}
export async function updateAnimalFoodDonations(args = {}) {
  const res = await fetchAPI(
    '/team/animal-food-donations-enabled',
    'put',
    args
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get corporate information for a team
 * @param {string} teamId
 * @returns {Promise<{isPartOfACorporate: boolean}>}
 */
export async function getTeamCorporateInfo(teamId) {
  const res = await fetchAPI(
    `/team/${teamId}/belongs-to-corporate`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

export async function updateDeliveryRadius(radius) {
  const res = await fetchAPI('/team/delivery-radius', METHODS.put, {
    deliveryRadius: radius,
  });
  await assertErrors(res);
  return await parseBody(res);
}

export async function getProvinces(country) {
  const res = await fetchAPI(
    `/team/province/${encodeURIComponent(country)}`,
    METHODS.get,
    undefined
  );
  await assertErrors(res);
  return await parseBody(res);
}

export async function getBlockedOrganizations({ offset, step, filterModel }) {
  const res = await fetchAPI(
    `/team/blocked-organizations?${qs.stringify({
      offset,
      step,
    })}`,
    'post',
    { filterModel }
  );
  await assertErrors(res);
  return await parseBody(res);
}

export async function addBlockedOrganization(data) {
  const res = await fetchAPI('/team/block-organization', METHODS.post, data);
  await assertErrors(res);
  return await parseBody(res);
}

export async function deleteBlockedOrganization(data) {
  const res = await fetchAPI('/team/block-organization', METHODS.delete, data);
  await assertErrors(res);
  return await parseBody(res);
}

export async function getOrganizationsToBlock({ lat, lng, search }) {
  const res = await fetchAPI(
    `/team/organizations-to-block?${qs.stringify({
      lat,
      lng,
      search,
    })}`,
    METHODS.get
  );
  await assertErrors(res);
  return await parseBody(res);
}

export async function getAcceptedFoodTypes() {
  const res = await fetchAPI(
    '/team/accepted-food-types',
    METHODS.get,
    undefined
  );
  await assertErrors(res);
  return await parseBody(res);
}

/**
 * Fetch preferred contacts for an organization.
 * @param {string} orgTeamId
 * @param {number} page
 * @param {number} pageSize
 * @param {object} filterModel - MUI DataGrid filter model
 * @returns Promise<{ contacts: PreferredContact[], count: number }>
 */
export async function fetchPreferredContacts(
  orgTeamId,
  page,
  pageSize,
  filterModel
) {
  const res = await fetchAPI(
    `/team/preferred-contacts/${orgTeamId}?${qs.stringify({
      page,
      pageSize,
    })}`,
    METHODS.post,
    { filterModel }
  );
  const body = await parseBody(res);
  return body;
}

/**
 * Add a preferred contact for an organization.
 * @param {Object} params
 * @param {string} params.id - The ID of the preferred contact.
 * @param {string} params.orgTeamId - The organization team ID associated with the preferred contact.
 * @param {string} params.name - The name of the preferred contact.
 * @param {string} params.title - The title of the preferred contact.
 * @param {string} params.phone - The phone number of the preferred contact.
 * @param {string} params.email - The email address of the preferred contact.
 */
export async function addPreferredContact({
  id,
  orgTeamId,
  name,
  title,
  phone,
  email,
  streetAddress,
  city,
  state,
  zipCode,
  notes,
  isResponsibleAgent,
  verificationStatus,
}) {
  const res = await fetchAPI(`/team/preferred-contacts`, METHODS.post, {
    id,
    orgTeamId,
    name,
    title,
    phone,
    email,
    streetAddress,
    city,
    state,
    zipCode,
    notes,
    isResponsibleAgent,
    verificationStatus,
  });
  await assertErrors(res);
}

/**
 * Update a preferred contact.
 * @param {Object} params
 * @param {string} params.id - The ID of the preferred contact.
 * @param {string} params.name - The name of the preferred contact.
 * @param {string} params.title - The title of the preferred contact.
 * @param {string} params.phone - The phone number of the preferred contact.
 * @param {string} params.email - The email address of the preferred contact.
 */
export async function updatePreferredContact({
  id,
  name,
  title,
  phone,
  email,
  streetAddress,
  city,
  state,
  zipCode,
  notes,
  isResponsibleAgent,
  verificationStatus,
  orgTeamId,
}) {
  const res = await fetchAPI(`/team/preferred-contacts/${id}`, METHODS.put, {
    name,
    title,
    phone,
    email,
    streetAddress,
    city,
    state,
    zipCode,
    notes,
    isResponsibleAgent,
    verificationStatus,
    orgTeamId,
  });
  await assertErrors(res);
}

/**
 * Delete a preferred contact.
 * @param {string} id - The ID of the preferred contact.
 */
export async function deletePreferredContact(id) {
  const res = await fetchAPI(`/team/preferred-contacts/${id}`, METHODS.delete);
  await assertErrors(res);
}

export async function updateOrgLegalName(orgTeamId, legalName) {
  const res = await fetchAPI(
    `/executive/org/legal-name/${orgTeamId}`,
    METHODS.put,
    {
      legalName,
    }
  );
  await assertErrors(res);
}

/**
 * Send an invite to invite a user as a freshcue member.
 * @param {object} invite
 * @param {string} invite.email
 * @param {string} invite.firstName
 * @param {string} invite.lastName
 * @returns {Promise<void>}
 */
export async function sendFreshcueInvite(invite) {
  const res = await fetchAPI(`/user/freshcue/invite`, METHODS.post, invite);
  await assertErrors(res);
}

export async function getOrgInstructions(teamId, type) {
  const res = await fetchAPI(
    `/team/org/${teamId}/instructions?type=${encodeURIComponent(type)}`,
    METHODS.get
  );
  const { instructions } = await parseBody(res);
  return instructions;
}

export async function upsertOrgInstructions(teamId, { type, instructions }) {
  const res = await fetchAPI(`/team/org/${teamId}/instructions`, METHODS.put, {
    type,
    instructions,
  });
  await assertErrors(res);
}

// Tax Receipts - Business/Donor

/**
 * Get available years for tax receipts.
 * @returns {Promise<{years: number[]}>}
 */
export async function getTaxReceiptYears() {
  const res = await fetchAPI('/team/tax-receipts/years', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get tax receipt data for a given year.
 * @param {number} year
 * @returns {Promise<{data: object, orgInfo: object, recipientInfo: object, receipt: object, year: number}>}
 */
export async function getTaxReceiptData(year) {
  const res = await fetchAPI(`/team/tax-receipts/${year}`, METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Generate tax receipts for selected recipients.
 * @param {number} year
 * @param {object} opts
 * @param {string[]} opts.recipientTeamIds - Array of recipient team IDs
 * @param {boolean} opts.forceRegenerate
 * @returns {Promise<object>}
 */
export async function generateTaxReceipts(
  year,
  { recipientTeamIds = [], forceRegenerate = false } = {}
) {
  const res = await fetchAPI(
    `/team/tax-receipts/${year}/generate`,
    METHODS.post,
    {
      recipientTeamIds,
      forceRegenerate,
    }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Download selected tax receipts as a ZIP file.
 * @param {number} year
 * @param {object} opts
 * @param {string[]} opts.recipientTeamIds - Array of recipient team IDs to download
 * @returns {Promise<Blob>}
 */
export async function downloadTaxReceiptsZip(
  year,
  { recipientTeamIds = [] } = {}
) {
  const res = await fetchAPI(
    `/team/tax-receipts/${year}/download`,
    METHODS.post,
    {
      recipientTeamIds,
    }
  );
  await assertErrors(res);
  return res.blob();
}

/**
 * Download a single tax receipt for a recipient.
 * @param {number} year
 * @param {string} recipientTeamId
 * @returns {Promise<{url: string}>}
 */
export async function downloadTaxReceipt(year, recipientTeamId) {
  const res = await fetchAPI(
    `/team/tax-receipts/${year}/${recipientTeamId}/download`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

// Tax Receipts - Food Mover

/**
 * Get available years for Food Mover donor receipts.
 * @returns {Promise<{years: number[]}>}
 */
export async function getFoodMoverTaxReceiptYears() {
  const res = await fetchAPI(
    '/team/food-mover/tax-receipts/years',
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Get all donor receipts for a Food Mover for a given year.
 * @param {number} year
 * @returns {Promise<{donors: Array, year: number}>}
 */
export async function getFoodMoverDonorReceipts(year) {
  const res = await fetchAPI(
    `/team/food-mover/tax-receipts/${year}`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Generate tax receipts for selected donors (for Food Mover view).
 * @param {number} year
 * @param {object} options
 * @param {string[]} options.donorTeamIds - Array of donor team IDs
 * @param {boolean} options.forceRegenerate - Force regeneration of existing receipts
 * @returns {Promise<{results: Array, year: number}>}
 */
export async function generateFoodMoverDonorReceipts(
  year,
  { donorTeamIds = [], forceRegenerate = false } = {}
) {
  const res = await fetchAPI(
    `/team/food-mover/tax-receipts/${year}/generate`,
    METHODS.post,
    { donorTeamIds, forceRegenerate }
  );
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Download multiple donor receipts as ZIP (for Food Mover view).
 * @param {number} year
 * @param {object} options
 * @param {string[]} options.donorTeamIds - Array of donor team IDs
 * @returns {Promise<Blob>}
 */
export async function downloadFoodMoverDonorReceiptsZip(
  year,
  { donorTeamIds = [] } = {}
) {
  const res = await fetchAPI(
    `/team/food-mover/tax-receipts/${year}/download`,
    METHODS.post,
    { donorTeamIds }
  );
  await assertErrors(res);
  return res.blob();
}

/**
 * Download a specific donor's receipt (for Food Mover view).
 * @param {number} year
 * @param {string} donorTeamId
 * @returns {Promise<{url: string}>}
 */
export async function downloadFoodMoverDonorReceipt(year, donorTeamId) {
  const res = await fetchAPI(
    `/team/food-mover/tax-receipts/${year}/${donorTeamId}/download`,
    METHODS.get
  );
  await assertErrors(res);
  return parseBody(res);
}

// Tax Contact and EIN

/**
 * Get tax contact info for an organization.
 * @returns {Promise<{taxContactName: string, taxContactEmail: string, taxContactPhone: string, ein: string}>}
 */
export async function getTaxContact() {
  const res = await fetchAPI('/team/tax-contact', METHODS.get);
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Update tax contact for an organization.
 * @param {object} data
 * @param {string} data.taxContactName
 * @param {string} data.taxContactEmail
 * @param {string} data.taxContactPhone
 * @returns {Promise<{success: boolean}>}
 */
export async function updateTaxContact({
  taxContactName,
  taxContactEmail,
  taxContactPhone,
}) {
  const res = await fetchAPI('/team/tax-contact', METHODS.put, {
    taxContactName,
    taxContactEmail,
    taxContactPhone,
  });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Update EIN for an organization.
 * @param {string} ein
 * @returns {Promise<{success: boolean, ein: string}>}
 */
export async function updateEIN(ein) {
  const res = await fetchAPI('/team/ein', METHODS.put, { ein });
  await assertErrors(res);
  return parseBody(res);
}

/**
 * Update tax receipt logo for an organization.
 * @param {string} taxReceiptLogoUrl - The S3 URL of the logo
 * @returns {Promise<{success: boolean}>}
 */
export async function updateTaxReceiptLogo(taxReceiptLogoUrl) {
  const res = await fetchAPI('/team/tax-receipt-logo', METHODS.put, {
    taxReceiptLogoUrl,
  });
  await assertErrors(res);
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                         Org service calendar                                */
/* -------------------------------------------------------------------------- */

/**
 * List org closed days / holidays (shared service calendar).
 * @param {{ fromDate?: string, toDate?: string }} [params]
 */
export async function listServiceCalendar(params) {
  const q = qs.stringify(params ?? {});
  const res = await fetchAPI(
    `/team/service-calendar${q ? `?${q}` : ''}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Add or update a closed day.
 * @param {{ exceptionDate: string, reason?: string }} data
 */
export async function upsertServiceCalendarDay(data) {
  const res = await fetchAPI('/team/service-calendar', METHODS.post, data);
  return parseBody(res);
}

/**
 * Remove a closed day.
 * @param {string} date YYYY-MM-DD
 */
export async function deleteServiceCalendarDay(date) {
  const res = await fetchAPI(
    `/team/service-calendar/${encodeURIComponent(date)}`,
    METHODS.delete
  );
  return parseBody(res);
}
